import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback, useState, memo } from 'react';
import { formatTimecode, formatTimeOnly } from '../utils/timecodeFormatter';
export const WaveformDisplay = memo(function WaveformDisplay(props) {
    const { waveformUrl, waveformColor, currentTime, duration, commentMarkers, fps, startFrame = 0, dropFrame = false, onSeekStart, onSeekEnd, } = props;
    const containerRef = useRef(null);
    const isDraggingRef = useRef(false);
    // Local drag state — used to update the playhead and tooltip during drag
    const [dragTime, setDragTime] = useState(null);
    const [dragX, setDragX] = useState(0);
    // Hover state — shows ghost playhead and tooltip on mouse hover
    const [hoverActive, setHoverActive] = useState(false);
    const [hoverTime, setHoverTime] = useState(0);
    const [hoverX, setHoverX] = useState(0);
    // --- Seeking ---
    const getTimeFromEvent = useCallback((clientX) => {
        const container = containerRef.current;
        if (!container || duration <= 0)
            return 0;
        const rect = container.getBoundingClientRect();
        const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        return fraction * duration;
    }, [duration]);
    const updateDrag = useCallback((clientX) => {
        const container = containerRef.current;
        setDragTime(getTimeFromEvent(clientX));
        if (container) {
            const rect = container.getBoundingClientRect();
            setDragX(clientX - rect.left);
        }
    }, [getTimeFromEvent]);
    const handleMouseDown = useCallback((e) => {
        isDraggingRef.current = true;
        updateDrag(e.clientX);
        onSeekStart();
        const onMouseMove = (ev) => {
            updateDrag(ev.clientX);
        };
        const onMouseUp = (ev) => {
            isDraggingRef.current = false;
            setDragTime(null);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            // Sync hover position to where the mouse was released so the
            // hover preview doesn't snap to a stale pre-drag position
            const container = containerRef.current;
            if (container) {
                const rect = container.getBoundingClientRect();
                const x = ev.clientX - rect.left;
                const fraction = Math.max(0, Math.min(1, x / rect.width));
                setHoverX(x);
                setHoverTime(fraction * duration);
                // Only show hover if mouse is still inside the waveform
                const inside = ev.clientX >= rect.left && ev.clientX <= rect.right
                    && ev.clientY >= rect.top && ev.clientY <= rect.bottom;
                setHoverActive(inside);
            }
            onSeekEnd(getTimeFromEvent(ev.clientX));
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [onSeekStart, onSeekEnd, getTimeFromEvent, updateDrag]);
    const handleTouchStart = useCallback((e) => {
        isDraggingRef.current = true;
        const touch = e.touches[0];
        if (touch)
            updateDrag(touch.clientX);
        onSeekStart();
        const onTouchMove = (ev) => {
            const t = ev.touches[0];
            if (t)
                updateDrag(t.clientX);
        };
        const onTouchEnd = (ev) => {
            isDraggingRef.current = false;
            setDragTime(null);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
            const t = ev.changedTouches[0];
            if (t)
                onSeekEnd(getTimeFromEvent(t.clientX));
        };
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
    }, [onSeekStart, onSeekEnd, getTimeFromEvent, updateDrag]);
    // Accessible range input handler
    const handleRangeChange = useCallback((e) => {
        const time = parseFloat(e.target.value);
        onSeekStart();
        onSeekEnd(time);
    }, [onSeekStart, onSeekEnd]);
    // --- Hover ---
    const handleMouseMove = useCallback((e) => {
        if (isDraggingRef.current)
            return;
        const container = containerRef.current;
        if (!container || duration <= 0)
            return;
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const fraction = Math.max(0, Math.min(1, x / rect.width));
        setHoverActive(true);
        setHoverX(x);
        setHoverTime(fraction * duration);
    }, [duration]);
    const handleMouseLeave = useCallback(() => {
        setHoverActive(false);
    }, []);
    // While dragging, show the drag position; otherwise show the actual playback position
    const displayTime = dragTime !== null ? dragTime : currentTime;
    const playheadPercent = duration > 0 ? (displayTime / duration) * 100 : 0;
    // Timecode tooltip while dragging
    const dragTimecode = dragTime !== null
        ? (fps ? formatTimecode(dragTime, fps, startFrame, dropFrame) : formatTimeOnly(dragTime))
        : '';
    // Hover timecode
    const hoverTimecode = hoverActive
        ? (fps ? formatTimecode(hoverTime, fps, startFrame, dropFrame) : formatTimeOnly(hoverTime))
        : '';
    // Clamp tooltip X so it doesn't overflow edges
    const tooltipHalfWidth = 40;
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const clampedDragX = containerWidth > 0
        ? Math.max(tooltipHalfWidth, Math.min(dragX, containerWidth - tooltipHalfWidth))
        : dragX;
    const clampedHoverX = containerWidth > 0
        ? Math.max(tooltipHalfWidth, Math.min(hoverX, containerWidth - tooltipHalfWidth))
        : hoverX;
    // Comment marker ticks
    const markers = commentMarkers?.filter((m) => m.reply <= 0 && !isNaN(m.pos)) ?? [];
    return (_jsxs("div", { className: "hekate-player-waveform", ref: containerRef, style: { backgroundColor: waveformColor || '#1a1a1a' }, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave, onTouchStart: handleTouchStart, children: [waveformUrl && (_jsx("img", { className: "hekate-player-waveform-image", src: waveformUrl, alt: "Audio waveform", draggable: false })), _jsx("div", { className: "hekate-player-waveform-played", style: { width: `${playheadPercent}%` } }), _jsx("div", { className: "hekate-player-waveform-playhead", style: { left: `${playheadPercent}%` } }), duration > 0 && markers.map((m) => (_jsx("div", { className: "hekate-player-waveform-marker", style: { left: `${(m.pos / duration) * 100}%` } }, m.id))), _jsx("input", { type: "range", className: "hekate-player-waveform-range", min: 0, max: duration, step: 0.001, value: currentTime, onChange: handleRangeChange, "aria-label": "Audio waveform seek" }), hoverActive && dragTime === null && (_jsxs(_Fragment, { children: [_jsx("div", { className: "hekate-player-waveform-hover-playhead", style: { left: `${hoverX}px` } }), _jsx("div", { className: "hekate-player-waveform-tooltip", style: { left: `${clampedHoverX}px`, transform: 'translateX(-50%)' }, "aria-hidden": "true", children: _jsx("div", { className: "hekate-player-waveform-tooltip-tc", children: hoverTimecode }) })] })), dragTime !== null && (_jsx("div", { className: "hekate-player-waveform-tooltip", style: { left: `${clampedDragX}px`, transform: 'translateX(-50%)' }, "aria-hidden": "true", children: _jsx("div", { className: "hekate-player-waveform-tooltip-tc", children: dragTimecode }) }))] }));
});
